import { Assert } from '@cotera/utilities';
import { BaseModel } from '../../actions/framework';
import { DG, EventStreamDefinition } from '@cotera/sdk/core';
import { ModelResource, TargetableModel } from '../../actions/framework/types';
import {
  ReadonlyWatchable,
  Watchable,
} from '../../actions/framework/watchable';
import { EraQL, Erod, Expression, Relation } from '@cotera/era';
import { ok, Result } from 'neverthrow';
import { getProgramEraQL } from './actions/utils';
import { Schedule } from '../../utils';
import { ToolSchema } from '@cotera/client/app/hooks/use-tool-schema';
import {
  exprOfFormValues,
  FormValues,
  formValuesOfExpr,
} from './erod-schema-form/form-values';

export type ToolCallColumnFormState = {
  name: string;
  tool: {
    name: string;
    id: string;
    description: string;
    method: Erod.Method;
    schema: Erod.Def<any>;
    output: Erod.Def<any>;
  } | null;
  parameters: FormValues | null;
  when: Schedule;
};

/**
 * The state of the Toolcall column.
 *
 * There are several key components:
 *  - Name
 *  - Selected Tool
 *  - Parameter Inputs
 *
 *
 * At the moment, only supports single parameter tool calls.
 */
export class ToolCallModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;
  isNew: boolean;

  readonly identity: ReadonlyWatchable<{
    rel: DG.Graph.Identity | null;
    col: DG.Graph.Identity | null;
  }>;

  readonly formState: Watchable<ToolCallColumnFormState>;

  readonly errors: ReadonlyWatchable<{
    programEraQL: Result<{}, any>;
    formValid: Result<{}, any>;
    parametersExpression: Result<{}, any>;
  }>;
  readonly preview = Watchable.fromValue<Relation | null>(null);
  readonly hasBeenTargeted: Watchable<boolean>;
  readonly changes: ReadonlyWatchable<Record<string, boolean>>;
  readonly pageTitle: ReadonlyWatchable<string>;
  readonly datasetRel: ReadonlyWatchable<Result<
    DG.Graph.RelItem,
    DG.Graph.GraphError
  > | null>;
  readonly datasetName: string;
  readonly currentNameInGraph: Watchable<string>;

  /**
   * The parameters represented (normally a struct Expression).
   */
  readonly parametersExpression: ReadonlyWatchable<Result<
    Expression,
    { t: string; message?: string }
  > | null>;
  readonly whereExpression: ReadonlyWatchable<Expression | null>;
  readonly programEraQL: ReadonlyWatchable<
    Result<string, { t: string; message?: string }>
  >;
  readonly eventStream: ReadonlyWatchable<EventStreamDefinition | null>;
  readonly hasErrors: ReadonlyWatchable<boolean>;
  readonly config: ReadonlyWatchable<DG.Graph.WorkflowColConfig | null>;

  constructor(
    readonly inputs: {
      datasetName: string;
      columnName: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      compiler: ReadonlyWatchable<DG.GraphCompiler>;
      toolSchemaLookup: (toolName: string) => ToolSchema | null;
    },
    opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
    } = {}
  ) {
    super(opts.id ?? `${inputs.datasetName}-${inputs.columnName}`);
    this.isNew = opts.isNew ?? false;

    this._isTargeted = opts.targeted ?? false;
    this.datasetName = inputs.datasetName;

    this.currentNameInGraph = Watchable.fromValue(inputs.columnName);

    this.pageTitle = Watchable.from((get) => {
      const name = get(this.currentNameInGraph);
      if (name === null) {
        return `${inputs.datasetName}/New Tool Call`;
      }
      return `${inputs.datasetName}/${name}`;
    });

    this.config = Watchable.from((get) => {
      const res =
        get(inputs.graph).getColumn({
          rel: inputs.datasetName,
          name: get(this.currentNameInGraph),
        })?.config ?? null;

      if (res === null) {
        return null;
      }

      Assert.assert(res.t === 'workflow');

      return res;
    });

    const toolConfig = getFormStateToolParameters(
      this.config.snapshot()!,
      inputs.graph.snapshot(),
      inputs.datasetName,
      inputs.toolSchemaLookup
    );

    const initialConfig = {
      name: inputs.columnName,
      tool: toolConfig && toolConfig.tool,
      parameters: toolConfig?.parameters ?? null,
      when: this.config.snapshot()?.when ?? 'never',
    };

    this.formState = Watchable.fromValue<{
      name: string;
      tool: {
        name: string;
        id: string;
        description: string;
        method: Erod.Method;
        schema: Erod.Def<any>;
        output: Erod.Def<any>;
      } | null;
      when: Schedule;
      parameters: FormValues | null;
    }>(initialConfig);

    this.hasBeenTargeted = Watchable.fromValue(false);

    /**
     * Whether there are changes to "formState"
     */
    this.changes = Watchable.from((get) => {
      const currentState = get(this.formState);

      if (this.isNew) {
        return {
          name: true,
          tool: true,
          parameters: true,
          when: true,
        };
      }

      const existingConfig = getFormStateToolParameters(
        get(this.config)!,
        get(inputs.graph),
        inputs.datasetName,
        inputs.toolSchemaLookup
      );

      if (existingConfig === null) {
        return {
          name: true,
          tool: true,
          parameters: true,
          when: true,
        };
      }

      return {
        name: currentState.name !== inputs.columnName,
        tool:
          JSON.stringify(currentState.tool) !==
          JSON.stringify(existingConfig.tool),
        parameters:
          JSON.stringify(currentState.parameters) !==
          JSON.stringify(existingConfig.parameters),
        when: currentState.when !== initialConfig.when,
      };
    });

    this.datasetRel = Watchable.from((get) => {
      const graph = get(inputs.graph);

      return (
        graph
          .forSymbol(inputs.datasetName)
          ?.builtNode.map((r) => {
            Assert.assert(r.item.t === 'rel');
            return r.item;
          })
          .mapErr((x) => x.err) ?? null
      );
    });

    this.parametersExpression = Watchable.from((get) => {
      const rel = get(this.datasetRel)?.match(
        (x) => x.rel,
        () => null
      );

      if (!rel) {
        return null;
      }

      const params = get(this.formState).parameters;

      if (params === null) {
        return null;
      }

      const schema = get(this.formState).tool?.schema;

      if (!schema) {
        return null;
      }

      const parser = get(inputs.graph).parser.forRel(this.datasetName);

      return exprOfFormValues(params, schema, parser, rel.ref('from'));
    });

    this.programEraQL = Watchable.from((get) => {
      const formState = get(this.formState);
      const promptExpr = get(this.parametersExpression);
      return (
        promptExpr?.andThen((expr) => getProgramEraQL(formState, expr)) ??
        getProgramEraQL(formState, null)
      );
    });

    this.errors = Watchable.from((get) => {
      const programEraQLRes = get(this.programEraQL).map((_s) => ({}));

      const formValidationRes = ok({}); // TODO

      const parametersExpression = get(this.parametersExpression);

      const parametersExprRes =
        parametersExpression === null
          ? ok({})
          : parametersExpression.map((_expr) => ({}));

      const errors = {
        programEraQL: programEraQLRes,
        formValid: formValidationRes,
        parametersExpression: parametersExprRes,
      };

      return errors;
    });

    this.eventStream = Watchable.from((get) => {
      const col = get(inputs.graph).getColumn({
        rel: inputs.datasetName,
        name: get(this.currentNameInGraph),
      });
      const es = col?.builtNode?.eventStream ?? null;
      return es;
    });

    this.hasErrors = Watchable.from((get) => {
      return Object.values(get(this.errors)).some((res) => res.isErr());
    });

    this.identity = Watchable.from((get) => {
      return {
        rel:
          get(inputs.graph).getRel({ symbol: inputs.datasetName })?.rawNode.meta
            .identity ?? null,
        col:
          get(inputs.graph).getColumn({
            rel: inputs.datasetName,
            name: get(this.currentNameInGraph),
          })?.meta.identity ?? null,
      };
    });
  }

  get isTargeted() {
    return this._isTargeted;
  }

  markAsStaged(): void {}

  target(): void {
    this._isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  hasChanges(): boolean {
    const changes = this.changes.snapshot();
    return Object.values(changes).some(Boolean);
  }

  resources(): ModelResource[] {
    return [
      {
        t: 'tab',
        tabRoute: `/dataset/${
          this.datasetName
        }/column/${this.currentNameInGraph.snapshot()}/atc`,
        name: 'StackFrame View',
        description: 'View the stack frames of the runs for this column.',
      },
    ];
  }
}

const getFormStateToolParameters = (
  config: DG.Graph.WorkflowColConfig,
  graph: DG.BuildExplorer,
  datasetName: string,
  toolSchemaLookup: (toolName: string) => ToolSchema | null
): Pick<ToolCallColumnFormState, 'tool' | 'parameters'> | null => {
  if (config === null) {
    return null;
  }

  const firstTool = Object.values(config.toolSymbolMap ?? {})[0];

  if (firstTool === undefined) {
    return null;
  }

  const fullToolName = firstTool.toolName.replaceAll('_', '/');

  const toolSchema = toolSchemaLookup(fullToolName);

  if (toolSchema === null) {
    return null;
  }

  const parser = graph.parser.forRel(datasetName);

  const matchesSingleToolFuncCall = (eraql: string): Expression | null => {
    const ast = EraQL.parseToEraQLAst(eraql);
    if (ast.isErr() || ast?.value?.[0].t !== 'call') {
      return null;
    }
    const [input, ...rest] = ast.value?.[0]?.args ?? [];
    if (input === undefined || rest.length > 0) {
      return null;
    }

    // Convert the extracted input AST node (already wrapped in WithMeta) back to a string and parse it
    const formatNode = EraQL.buildNode(input);
    const inputEraql = EraQL.generateProgram(formatNode);

    return parser.parseExpr(inputEraql).match(
      (x) => x,
      () => null
    );
  };

  const expr = matchesSingleToolFuncCall(config.source);

  const erodParams = toolSchema.inputSchema;

  if (expr === null) {
    return null;
  }

  const params = formValuesOfExpr(expr, erodParams);

  return {
    tool: {
      name: firstTool.toolName,
      id: firstTool.toolId,
      description: toolSchema.description,
      method: toolSchema.method,
      schema: erodParams,
      output: toolSchema.outputSchema,
    },
    parameters: params,
  };
};
